// Email the team when open house feedback comes in (Henry, 2026-08-31: "we
// need to get an email when someoen siubits the open hosue feedback").
// Recipients: the listing agent plus the owner (OWNER_EMAILS), minus whoever
// submitted it. This module handles the in-app door (FeedbackModal); the
// public no-login form is handled server-side in functions-api, which keeps
// its own copy of these builders — change the wording in BOTH places.

import { OWNER_EMAILS } from "./brokerageSettings";
import { sendOpenHouseEmail } from "./emailSender";

export function resolveFeedbackEmailRecipients({
  openHouse,
  allUsers,
  excludeEmails = [],
}) {
  const active = (allUsers || []).filter(
    (u) => u && u.type !== 1 && u.type !== 2,
  );
  const ownerEmails = OWNER_EMAILS.map((e) => e.toLowerCase());
  const targets = active.filter((u) => {
    const email = String(u.email || "").trim().toLowerCase();
    if (!email) return false;
    return u.id === openHouse?.listingAgentId || ownerEmails.includes(email);
  });
  const excluded = excludeEmails
    .map((e) => String(e || "").trim().toLowerCase())
    .filter(Boolean);
  // Dedupe by email so an owner who is also the listing agent gets one copy.
  const seen = new Set();
  return targets.filter((u) => {
    const email = String(u.email).trim().toLowerCase();
    if (excluded.includes(email) || seen.has(email)) return false;
    seen.add(email);
    return true;
  });
}

// "2026-09-05" → "09/05/2026". String work only — running the input through
// Date() would shift it a day for some timezones.
function fmtDateMDY(iso) {
  const [y, m, d] = String(iso || "").split("-");
  if (!y || !m || !d) return String(iso || "");
  return `${m}/${d}/${y}`;
}

export function buildFeedbackEmail({ openHouse, feedback }) {
  const shortAddr =
    openHouse?.propertyAddress?.split(",")[0] || "a listing";
  const otherBlock =
    feedback?.otherComments && String(feedback.otherComments).trim()
      ? `\nOther comments:\n${String(feedback.otherComments).trim()}\n`
      : "";
  const text = `${feedback?.agentName || "The hosting agent"} submitted open house feedback.

Listing: ${openHouse?.propertyAddress || shortAddr}
Held: ${fmtDateMDY(feedback?.date)}${feedback?.time ? `, ${feedback.time}` : ""}
Groups through: ${feedback?.groups ?? 0}
Second showings requested: ${feedback?.secondShowings ?? 0}

What agents said:
${feedback?.agentFeedback || "-"}

What buyers said:
${feedback?.buyerFeedback || "-"}
${otherBlock}
Thanks,
Team Edelstein`;
  return { subject: `Open House Feedback: ${shortAddr}`, text };
}

// Fire-and-forget from FeedbackModal after the feedback saves. Sends as the
// submitting user (their Gmail if connected, the team account otherwise) and
// never throws — saving feedback and composing the recap must not depend on
// this email going out.
export async function emailTeamOnOhFeedback({
  openHouse,
  feedback,
  allUsers,
  sender,
}) {
  const recipients = resolveFeedbackEmailRecipients({
    openHouse,
    allUsers,
    excludeEmails: [sender?.email],
  });
  if (!recipients.length) return;
  const { subject, text } = buildFeedbackEmail({ openHouse, feedback });
  const results = await Promise.allSettled(
    recipients.map((u) =>
      sendOpenHouseEmail({
        userId: sender?.id,
        to: u.email,
        toName: `${u.firstname || ""} ${u.lastname || ""}`.trim(),
        subject,
        text,
        type: "oh-feedback",
        relatedOpenHouseId: String(openHouse?.id || ""),
      }),
    ),
  );
  results.forEach((r) => {
    if (r.status === "rejected") {
      console.error("OH feedback email failed:", r.reason);
    } else if (r.value && r.value.ok === false) {
      console.error("OH feedback email failed:", r.value.error);
    }
  });
}
